import React, { useState } from 'react';
import { RuleModel } from 'src/types/rule.model';
import { matchesRule } from 'src/utils/rule.util';
import Typography from 'src/components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import Selectors from 'src/utils/selectors';
import LiteralsService from 'src/services/literals/LiteralsService';
import { RuleMatcher } from 'src/types/ruleMatcher.enum';
import { trackClick } from 'src/hooks/useAdobeTags';
import adobeTags from 'src/types/adobe-tags';
import CreateTransactionRulePrompt from 'src/components/atomic/molecules/createTransactionRulePrompt/CreateTransactionRulePrompt';
import { TransactionModel } from 'src/types/transaction.model';
import { useParams } from 'react-router-dom';
import { useLocation } from 'react-router';
import { useTransaction } from 'src/hooks/queries/transactions';
import { useDeleteRule, useRules, useUpdateRule } from 'src/hooks/queries/rules';
import CreateUpdateRuleFormikWrapper from 'src/components/atomic/molecules/createUpdateRule/CreateUpdateRule';
import styles from './transactionRules.module.scss';
import { useCategories } from 'src/hooks/queries/categories';
import { CategoryModel } from 'src/types/category.model';
import { useBreakpoints } from 'src/hooks/useBreakpoints';
import { getCategoryName } from 'src/utils/category.utils';

export default function TransactionRulesWrapper(): JSX.Element {
    const params = useParams();
    const { pathname } = useLocation();
    const { isLoading: isLoadingTransaction, data: transaction } = useTransaction(Number(params.id));
    const { isLoading: isLoadingRules, data: rules } = useRules();
    const { isLoading: isLoadingCategories, data: categories } = useCategories();
    const { isMinWidthMedium } = useBreakpoints();
    const { mutateAsync: updateRule } = useUpdateRule();
    const { mutateAsync: deleteRule } = useDeleteRule();
    const [showCreateRule, setShowCreateRule] = useState<boolean>(false);

    const isLoading = isLoadingTransaction || isLoadingRules || isLoadingCategories;
    const handleSubmit = async (newRule: Partial<RuleModel>): Promise<void> => {
        await updateRule(newRule);
        trackClick(pathname, adobeTags.Transactions.Details.Apply);
        if (!matchesRule(transaction, newRule)) {
            setShowCreateRule(false);
        }
    };

    const handleDelete = async (rule: RuleModel): Promise<void> => {
        await deleteRule(rule?.id);
        trackClick(pathname, adobeTags.Rules.Delete);
        setShowCreateRule(false);
    };

    const handleOnClickCreateRuleButton = (): void => {
        setShowCreateRule(true);
        trackClick(pathname, adobeTags.Transactions.Details.Create);
    };

    return (
        <TransactionRules
            rules={rules}
            categories={categories}
            transaction={transaction}
            showCreateRule={showCreateRule}
            handleOnClickCreateRuleButton={handleOnClickCreateRuleButton}
            pathname={pathname}
            isLoading={isLoading}
            isMinWidthMedium={isMinWidthMedium}
            handleSubmit={handleSubmit}
            handleDelete={handleDelete}
        />
    );
}

export interface TransactionRulesProps {
    rules: RuleModel[];
    categories: CategoryModel[];
    transaction: TransactionModel;
    showCreateRule?: boolean;
    pathname: string;
    isLoading?: boolean;
    isMinWidthMedium?: boolean;
    handleSubmit: (rule: Partial<RuleModel>) => Promise<void>;
    handleDelete: (rule: RuleModel) => Promise<void>;
    handleOnClickCreateRuleButton: () => void;
}

export function TransactionRules({
    rules,
    categories,
    transaction,
    pathname,
    showCreateRule,
    handleSubmit,
    handleDelete,
    handleOnClickCreateRuleButton,
    isLoading,
    isMinWidthMedium
}: TransactionRulesProps): JSX.Element {
    if (isLoading) return <div data-cy={Selectors.General.LOADING_INDICATOR} />;

    const matchingRules: RuleModel[] = rules.filter((rule: RuleModel) => matchesRule(transaction, rule));

    const heading: JSX.Element = (
        <Typography
            variant={TypographyVariants.BODY_BOLD}
            component="h2"
            data-cy={Selectors.Transactions.CREATE_RULE_HEADER}
        >
            {LiteralsService.getLiteral('RuleRow.createTransactionRule')}
        </Typography>
    );

    const newRule: Partial<RuleModel> = {
        transactionName: transaction.merchantName,
        matcher: RuleMatcher.CONTAINS
    };

    if (matchingRules.length) {
        return (
            <>
                {heading}
                <div className={styles.matchingRulesContainer} data-cy={Selectors.Transactions.MATCHING_RULES}>
                    {matchingRules.map((rule, index) => (
                        <CreateUpdateRuleFormikWrapper
                            rule={rule}
                            rules={rules}
                            ruleCategoryName={getCategoryName(rule.groupUid, categories)}
                            key={index}
                            transaction={transaction}
                            categories={categories}
                            isMinWidthMedium={isMinWidthMedium}
                            pathname={pathname}
                            handleSubmit={handleSubmit}
                            handleDelete={handleDelete}
                        />
                    ))}
                </div>
            </>
        );
    }

    return (
        <>
            {heading}
            {showCreateRule ? (
                <CreateUpdateRuleFormikWrapper
                    rule={newRule}
                    rules={rules}
                    transaction={transaction}
                    categories={categories}
                    isCreatingRule
                    isMinWidthMedium={isMinWidthMedium}
                    pathname={pathname}
                    handleSubmit={handleSubmit}
                    handleDelete={handleDelete}
                />
            ) : (
                <CreateTransactionRulePrompt handleOnClick={handleOnClickCreateRuleButton} />
            )}
        </>
    );
}
